// ─────────────────────────────────────────────────────────────────────────────
// INSTRUMENT: Learning Agility Assessment
// Framework:  Korn Ferry / Lominger-inspired · 5 dimensions · 40 items
// Output:     MA/PA/CA/RA/SA scores, overall LA score, profile type, band
// ─────────────────────────────────────────────────────────────────────────────
import { QUESTIONS, SCALE_OPTIONS, QUESTIONS_PER_PAGE, TOTAL_PAGES } from "./questions.js";
import { DIMENSIONS, DIM_MAP, LA_BANDS, NORMS } from "./factors.js";
import { scoreLA } from "./scoring.js";

function buildAIPrompt(assessment) {
  const sc = assessment.scores || {};
  const overall = assessment.overall || assessment.perfAvg || 0;
  const band    = assessment.band || {};
  const dominant   = assessment.dominant || "—";
  const developing = assessment.developing || "—";
  const profile    = assessment.profileType || "—";

  const dimLines = DIMENSIONS.map(d => {
    const s = sc[d.id] != null ? sc[d.id].toFixed(1) : "—";
    const nrp = assessment.nrp?.[d.id] || "—";
    return `  • ${d.id} ${d.name}: ${s}/10 (${nrp} norm) — ${d.headline}`;
  }).join("\n");

  return `You are a world-class leadership development expert interpreting a Learning Agility Assessment inspired by the Korn Ferry model.

LEARNING AGILITY REPORT — provide a deep, personalised development narrative covering: (1) overall learning agility profile and what it means for this person's career ceiling, (2) deep-dive on each of the 5 dimensions, (3) the specific development priority for the lowest dimension, (4) how to leverage the dominant dimension as a bridge to developing the weakest.

INSTRUMENT: Learning Agility Assessment (40-item Likert, 5 dimensions)
CANDIDATE: ${assessment.name} | ${assessment.role || ""} | ${assessment.demo?.company || ""}

OVERALL LEARNING AGILITY SCORE: ${overall}/10 — ${band.label || ""} (${band.sub || ""})
PROFILE TYPE: ${profile}
DOMINANT DIMENSION: ${dominant}
DEVELOPING DIMENSION: ${developing}

DIMENSION SCORES:
${dimLines}

DIMENSION DEFINITIONS:
MA — Mental Agility: Thinks critically, comfortable with complexity, questions assumptions, makes fresh connections.
PA — People Agility: Reads situations, learns from others, navigates diversity, handles conflict constructively.
CA — Change Agility: Energised by novelty, experiments readily, tolerates ambiguity, seeks variety.
RA — Results Agility: Delivers in first-time situations, inspires others, resourceful under pressure.
SA — Self-Awareness: Accurate self-knowledge, seeks feedback actively, non-defensive, calibrated.

NOTE: Learning agility is the single strongest predictor of executive potential and long-term career success (Korn Ferry, 2014). A score above 7.0 in overall LA correlates strongly with high-potential talent identification. The developing dimension is almost always more actionable than the dominant one. Please give specific, practical development advice.`;
}

const LA = {
  id:          "la",
  name:        "Learning Agility Assessment",
  shortName:   "LA",
  version:     "1.0",
  publisher:   "AXIS360 — Korn Ferry-inspired Framework",
  description: "40-item Learning Agility Assessment measuring 5 dimensions: Mental Agility, People Agility, Change Agility, Results Agility and Self-Awareness. Identifies overall LA band, profile type, dominant dimension and key development priorities. The strongest single predictor of leadership potential and career success.",
  icon:        "⚡",
  color:       "#c8964e",
  bg:          "#fff7ed",

  totalQuestions: 40,
  timeEstimate:   "10–15 min",
  scaleType:      "likert-5",
  pagesOf:        QUESTIONS_PER_PAGE,
  totalPages:     TOTAL_PAGES,

  questions:   QUESTIONS,
  scales:      DIMENSIONS,
  scaleMap:    DIM_MAP,
  bands:       LA_BANDS,
  norms:       NORMS,
  scaleOptions: SCALE_OPTIONS,

  nineboxAxes: {
    x: { label: "Results Agility",   description: "Drive to deliver in first-time situations", scaleIds: ["RA"] },
    y: { label: "Mental Agility",    description: "Ability to think through complexity",        scaleIds: ["MA"] },
  },
  nineboxCells: null,
  competencies: null,

  score:         scoreLA,
  buildAIPrompt,

  answerFormat: {
    type:    "likert-5",
    options: [
      { value: 1, label: "Strongly Disagree" },
      { value: 2, label: "Disagree" },
      { value: 3, label: "Neutral" },
      { value: 4, label: "Agree" },
      { value: 5, label: "Strongly Agree" },
    ],
  },
};

export default LA;
